import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Upload, X, FileText, AlertCircle, Info, CalendarIcon } from 'lucide-react';
import { useUploadPolicyVersion } from '@/hooks/useUploadPolicyVersion';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { addMonths, format } from 'date-fns';
import { cn } from '@/lib/utils';

interface UploadVersionDialogProps {
  policyId: string;
  policyName: string;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export function UploadVersionDialog({ policyId, policyName }: UploadVersionDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubstantive, setIsSubstantive] = useState(false);
  const [nextReviewDate, setNextReviewDate] = useState<Date | undefined>(addMonths(new Date(), 12));
  const [versionNotes, setVersionNotes] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const { uploadVersion, isUploading, uploadProgress, error, reset } = useUploadPolicyVersion();


  function validateFile(file: File): string | null {
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf'];
    
    if (!allowedExtensions.includes(ext || '')) {
      return 'Please upload a PDF. Working in Word? Use File → Export → PDF, then upload here.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 100MB.';
    }
    return null;
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setFileError(validationError);
      setSelectedFile(null);
    } else {
      setFileError(null);
      setSelectedFile(file);
    }
  }

  function handleRemoveFile() {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function handleUpload() {
    if (!selectedFile) return;

    uploadVersion(
      { 
        policyId, 
        file: selectedFile,
        isSubstantive,
        nextReviewDate: isSubstantive && nextReviewDate ? format(nextReviewDate, 'yyyy-MM-dd') : undefined,
        versionNotes: versionNotes.trim() || undefined,
      },
      {
        onSuccess: (data) => {
          const ackMessage = isSubstantive 
            ? 'Staff will be notified to acknowledge after approval.'
            : 'No staff action needed.';
          toast({
            title: 'Version uploaded and pending approval',
            description: `Version ${data.version} has been submitted. ${ackMessage}`,
          });
          handleClose();
        },
        onError: (err) => {
          toast({
            title: 'Upload failed',
            description: err.message,
            variant: 'destructive',
          });
        },
      }
    );
  }

  function handleClose() {
    setOpen(false);
    setSelectedFile(null);
    setFileError(null);
    setIsSubstantive(false);
    setNextReviewDate(addMonths(new Date(), 12));
    setVersionNotes('');
    reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      handleClose();
    } else {
      setOpen(true);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-2 hover:bg-[#2d8585] transition-colors">
          <Upload className="h-4 w-4" />
          Upload New Version
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload New Version</DialogTitle>
          <DialogDescription>
            Upload a new version for "{policyName}". The version will require approval before becoming active.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-4">
          {/* File Upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            className="hidden"
            id="version-file-upload"
          />
          {!selectedFile ? (
            <div className="relative">
              <Label className="mb-2 block">Document</Label>
              <label
                htmlFor="version-file-upload"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragOver(false);
                  const droppedFile = e.dataTransfer.files?.[0];
                  if (droppedFile) {
                    const validationError = validateFile(droppedFile);
                    if (validationError) {
                      setFileError(validationError);
                      setSelectedFile(null);
                    } else {
                      setFileError(null);
                      setSelectedFile(droppedFile);
                    }
                  }
                }}
                className={cn(
                  "flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors",
                  isDragOver ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                )}
              >
                <Upload className="h-6 w-6 text-muted-foreground mb-1.5" />
                <span className="text-sm text-muted-foreground">Click or drag to upload</span>
                <span className="text-xs text-muted-foreground mt-0.5">PDF only, max 100MB · In Word? File → Export → PDF</span>
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-md border bg-muted/50 p-3">
              <div className="flex items-center gap-3 overflow-hidden">
                <FileText className="h-5 w-5 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRemoveFile}
                disabled={isUploading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Substantive Update Checkbox */}
          <div className="flex items-start gap-3">
            <Checkbox
              id="substantive"
              checked={isSubstantive}
              onCheckedChange={(checked) => setIsSubstantive(checked === true)}
              disabled={isUploading}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="substantive"
                className="text-sm font-medium cursor-pointer"
              >
                This is a substantive update (requires staff re-acknowledgment)
              </Label>
              <p className="text-xs text-muted-foreground">
                Check this if the policy content has changed significantly
              </p>
            </div>
          </div>

          {/* Conditional: Next Review Date picker */}
          {isSubstantive && (
            <div className="rounded-md border bg-muted/30 p-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Next Review Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !nextReviewDate && "text-muted-foreground"
                      )}
                      disabled={isUploading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {nextReviewDate ? format(nextReviewDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={nextReviewDate}
                      onSelect={setNextReviewDate}
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  Defaults to 12 months from today. Pick any date to override.
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-primary">
                <Info className="h-4 w-4" />
                Staff will be notified to acknowledge after approval
              </div>
            </div>
          )}

          {/* Minor update notice */}
          {!isSubstantive && selectedFile && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 rounded-md p-3">
              <Info className="h-4 w-4 shrink-0" />
              Minor update: Review schedule will not change and no re-acknowledgment required.
            </div>
          )}

          {/* Version Notes */}
          <div className="space-y-2">
            <Label htmlFor="version-notes">Version Notes (optional)</Label>
            <Textarea
              id="version-notes"
              placeholder="Add notes about changes in this version..."
              value={versionNotes}
              onChange={(e) => setVersionNotes(e.target.value)}
              disabled={isUploading}
              rows={3}
            />
          </div>

          {fileError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {fileError}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {error.message}
            </div>
          )}

          {isUploading && (
            <div className="space-y-2">
              <Progress value={uploadProgress} />
              <p className="text-center text-sm text-muted-foreground">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isUploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!selectedFile || isUploading}>
              {isUploading ? 'Uploading...' : 'Upload & Submit for Approval'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
